import { HydraUIAPIClient } from '@customerio/hydra-ui-api';
import { getAuthHeaders } from './auth-headers';
import { getApiBaseUrl, getApiBaseUrlForDataCenter, getUsApiBaseUrl } from './base-url';
import type { ApiDataCenter } from './base-url';
import { withSampleWorkspaceHost } from './sample-workspace-host';
import { recordRumRequest } from '@/services/rum';
import { walletSimulatorFetch } from '@/services/dev-helper/wallet-simulator';

export const hydraUiApi = new HydraUIAPIClient({
  baseUrl: getApiBaseUrl,
  fetch: withSampleWorkspaceHost(walletSimulatorFetch),
  fetchOptions: { credentials: 'include' },
  headers: getAuthHeaders,
  onRequestTiming: recordRumRequest,
});

/**
 * Variant of {@link hydraUiApi} pinned to the US data center. Use it only for endpoints that are
 * served exclusively from, or must be routed to, the US region; every other endpoint must use
 * {@link hydraUiApi}. Current callers:
 *
 * - the SSO/SCIM configuration endpoints, which are US-only (opsbugs/4138);
 * - the contracts-sales support inboxes, and the copilot account-management tool that posts to the
 *   same premium inbox, so every sales lead lands in one queue rather than depending on the account's
 *   region running its own support-ticket consumer;
 * - the Vercel install popup (`pages/vercel-install`): its install session is verified against the
 *   global primary, and a logged-in EU user's regional client would read the EU replica inside
 *   replication lag and get a 401.
 *
 * The remaining support paths (`support.create`, `support.feedback`, `support.sign`) still use the
 * regional client and are a known inconsistency — see the note in `use-submit-contracts-sales.ts`.
 */
export const hydraUiApiUs = new HydraUIAPIClient({
  baseUrl: getUsApiBaseUrl,
  fetchOptions: { credentials: 'include' },
  headers: getAuthHeaders,
  onRequestTiming: recordRumRequest,
});

/**
 * Variant of {@link hydraUiApi} pinned to the EU data center. Its only caller is the internal CLI
 * tool (`pages/internal-tools/cio-cli-tool`), where the operator picks the region a command runs
 * in; reach it through {@link hydraUiApiForDataCenter} rather than directly.
 */
export const hydraUiApiEu = new HydraUIAPIClient({
  baseUrl: () => getApiBaseUrlForDataCenter('eu'),
  fetchOptions: { credentials: 'include' },
  headers: getAuthHeaders,
  onRequestTiming: recordRumRequest,
});

/** The client pinned to `dataCenter`, for a request whose region the operator chooses. */
export function hydraUiApiForDataCenter(dataCenter: ApiDataCenter) {
  return dataCenter === 'eu' ? hydraUiApiEu : hydraUiApiUs;
}
